<template>
  <ion-modal
      :is-open="props.isDrawerOpen"
      @didDismiss="emit('close')"
      class="responsive-drawer-modal"
      :backdrop-dismiss="true"
      :enter-animation="customEnterAnimation"
      :leave-animation="customLeaveAnimation"
  >
    <ion-header class="ion-no-border drawer-toolbar-header">
      <div class="popover-profile-header">
        <div class="profile-header" @click="closeDrawerAndGoTo('/settings')">
          <suite-avatar
              :src="settingsStore.userProfileImage"
              :name="`${userData?.FirstName || ''} ${userData?.LastName || ''}`"
              class="user-avatar"
              size="md"
          />
          <div class="user-info">
            <span class="user-name">{{ userData.FirstName }} {{ userData.LastName }}</span>
            <div v-if="dailyCode" class="user-pin-code">{{t('common.dailyCode')}}: {{dailyCode}}</div>
          </div>
          <suite-button>
            <i class="fa-solid fa-gear"></i>
          </suite-button>
        </div>
        <suite-app-card
            v-if="rootService"
            :service="rootService"
            @click="closeDrawerAndGoTo(rootService.url)"
        />
      </div>
    </ion-header>

    <ion-content class="drawer-main-content">
      <div class="drawer-inner-wrapper">
        <suite-app-section v-if="false" :title="t('home.recentApps')">
          <horizontal-scroll fade-color="#f8fafc" fade-width="50px">
            <suite-app-card
                v-for="service in recentServices"
                :key="service.id"
                :service="service"
                @click="closeDrawerAndGoTo(service.url)"
                mini
            />
          </horizontal-scroll>
        </suite-app-section>

        <suite-app-section>
          <template #header>
            <h2 class="suite-app-section-title">{{ t('home.yourApps') }}</h2>
          </template>
          <template v-if="false" #header-actions>
            <suite-input fa-icon="fa-magnifying-glass" focus-width="260px" :placeholder="t('common.search')" v-model="searchQuery" />
          </template>
          <suite-app-card
            v-for="service in filteredServices"
            :key="service.id"
            :service="service"
            @click="closeDrawerAndGoTo(service.url)"
            :single-line="true"
          />
          <div v-if="filteredServices.length === 0" class="drawer-empty-search-state">
            <p>{{ t('home.noServicesToShow') }}</p>
          </div>
        </suite-app-section>

      </div>
    </ion-content>
  </ion-modal>
</template>

<script setup lang="ts">
import {
  IonModal,
  IonHeader,
  IonContent,
} from '@ionic/vue';
import SuiteAppCard from "@/components/SuiteAppCard.vue";
import {useServices} from "@/composables/useServices.ts";
import {useNavigation} from "@/composables/useNavigation.ts";
import SuiteAppSection from "@/components/suiteAppSection.vue";
import {useSearch} from "@/composables/useSearch.ts";
import SuiteInput from "@/components/SuiteInput.vue";
import HorizontalScroll from "@/components/HorizontalScroll.vue";
import {useLocale} from "@/composables/useLocale.ts";
import {customEnterAnimation, customLeaveAnimation} from "@/utils/drawerAnimations.ts";
import {useSettingsStore} from "@/stores/useSettingsStore.ts";
import SuiteButton from "@/components/SuiteButton.vue";

import SuiteAvatar from "@/components/SuiteAvatar.vue";
import {useAuth} from "@/composables/useAuth.ts";

const props = defineProps({
  isDrawerOpen: { type: Boolean, default: false }
});

const settingsStore = useSettingsStore()

const { userData, dailyCode } = useAuth();
const { t } = useLocale();
const { goTo } = useNavigation();
const {services, rootService, recentServices} = useServices();

const { searchQuery, filteredItems: filteredServices } = useSearch(services, ["label", "description"]);

const emit = defineEmits(['close']);

const closeDrawerAndGoTo = (url: string) => {
  goTo(url);
  emit('close');
}
</script>

<style scoped>
.responsive-drawer-modal {
  --width: 100%;
  --height: 100%;
  --max-width: 100%;
  --max-height: 100%;
  --border-radius: 0px;
  position: absolute;
  width: 100%;
  height: calc(100% - var(--navbar-height) - env(safe-area-inset-top));
  top: calc(var(--navbar-height) + env(safe-area-inset-top));
}

.popover-profile-header {
  display: flex;
  flex-direction: column;
  padding: 20px 16px 8px;
  gap: 8px;
  border-bottom: none;
}

.profile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-inline: 8px;
}

.responsive-drawer-modal .drawer-toolbar-header {
  background: #F4F4F4;
  border-bottom: 1px solid #e2e8f0;
}

.responsive-drawer-modal > div {
  box-shadow: inset 0px 3px 6px #0000001f;
}

.drawer-toolbar-header ion-toolbar {
  --background: #ffffff;
  --color: #363131;
  --padding-start: 20px;
  --padding-end: 16px;
  height: 64px;
}

.drawer-main-content {
  --background: #ffffff;
}

.drawer-inner-wrapper {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.drawer-empty-search-state {
  text-align: center;
  padding: 20px 0;
  color: #ffffff;
}

.suite-app-section-title {
  font-size: 1rem;
  font-weight: 600;
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.user-name {
  color: #2c2c2c;
  font-size: 1.2rem;
  letter-spacing: -0.2px;
  font-weight: 600;
}

.user-pin-code {
  font-size: .8rem;
  color: var(--primary-color);
  font-weight: 500;
}

@media (min-width: 768px) {
  .responsive-drawer-modal {
    --width: 440px;
    --max-width: 440px;
  }

  .popover-profile-header {
    padding: 20px 8px 8px;
  }

  .responsive-drawer-modal::part(content) {
    position: absolute;
    inset-inline-end: 0;
    height: 100%;
    border-left: 1px solid #e2e8f0;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
  }

  .drawer-inner-wrapper {
    padding: .5rem;
  }
}

</style>